import { withdrawsOnRevise } from "./draftActions";
import { sameEmail } from "./recipients/recipients";
import { useSendConnection } from "./sendConnection";
import {
  Proposal,
  ProposalRecipient,
  ProposalRecipientRole,
  ProposalRecipients,
  ProposalRecipientStatus,
  ProposalStatus,
  RecipientSigningState
} from "./types";

// The e-sign scan reads the vendor every 15 minutes; checking more often than
// this only re-reads what is already shown.
export const TRACKING_POLL_MS = 60_000;

// Status comes in through the account's PandaDoc connection; once it needs
// reconnecting, nothing new arrives. Unknown while the connection loads, or
// when it can't be read.
export const useTrackingPaused = () => {
  const { held, missing } = useSendConnection();
  return held && missing !== undefined;
};

// Still moving at the vendor: the same request a Revise would withdraw.
export const isTracked = withdrawsOnRevise;

const recipientStatus = (
  proposalStatus: ProposalStatus,
  role: ProposalRecipientRole,
  state: RecipientSigningState | undefined
): ProposalRecipientStatus => {
  // A copy has nothing to sign, though PandaDoc marks it completed with the
  // signers: it only ever reads as sent, or viewed once opened.
  if (role === ProposalRecipientRole.COPY) {
    return state?.viewedAt ? ProposalStatus.DELIVERED : ProposalStatus.SENT;
  }
  // PandaDoc marks a decliner completed too, so the decline is read first.
  if (state?.declinedAt) return ProposalStatus.DECLINED;
  if (state?.signed) return ProposalStatus.SIGNED;
  // A signer withdrawn from before they signed; a copy had nothing to sign.
  if (
    proposalStatus === ProposalStatus.VOIDED &&
    role === ProposalRecipientRole.SIGNER
  ) {
    return ProposalStatus.VOIDED;
  }
  if (state?.viewedAt) return ProposalStatus.DELIVERED;
  return ProposalStatus.SENT;
};

// Each recipient's state as the vendor last reported it, matched by email. A
// draft has nothing to report yet.
export const withRecipientStates = (
  recipients: ProposalRecipients,
  proposal: Pick<Proposal, "status" | "recipientStates">
): ProposalRecipients => {
  if (proposal.status === ProposalStatus.DRAFT) return recipients;
  const states = proposal.recipientStates ?? [];
  return {
    ...recipients,
    list: recipients.list.map((recipient): ProposalRecipient => {
      const state = states.find((candidate) =>
        sameEmail(candidate.email, recipient.email)
      );
      const status = recipientStatus(proposal.status, recipient.role, state);
      return {
        ...recipient,
        status,
        ...(status === ProposalStatus.DECLINED
          ? {
              declinedAt: state?.declinedAt,
              declineReason: state?.declineReason
            }
          : {})
      };
    })
  };
};
